import * as React from 'react'
import { useEffect, useState } from 'react'
import clsx from 'clsx'
import Lottie from 'lottie-react'
import { useThemeContext } from 'hooks/context/useThemeContext'

// Define animation types for lazy loading
const animationNames = [
  'welcome',
  'no-data',
  'no-results',
  'not-verified',
  'verified',
  'connection-error',
  'permissions-denied',
  'app-loader',
] as const

export type AnimationName = (typeof animationNames)[number]

export interface LottieProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Animation data object or URL to the Lottie animation JSON file
   */
  src: object | AnimationName
  /**
   * Auto-play animation when component loads
   */
  autoplay?: boolean
  /**
   * Loop animation continuously
   */
  loop?: boolean
  /**
   * Width of the animation container
   */
  width?: number | string
  /**
   * Height of the animation container
   */
  height?: number | string
}

const SimpleLottie: React.FC<LottieProps> = ({
  src,
  autoplay = true,
  loop = false,
  width = 200,
  height = 200,
  className,
  ...props
}) => {
  const { darkMode } = useThemeContext()
  const [animation, setAnimation] = useState<object | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    // If src is already an object, use it directly
    if (typeof src === 'object') {
      setAnimation(src)
      return
    }

    // Otherwise, fetch the animation from the remote URL
    const loadAnimation = async () => {
      setIsLoading(true)
      try {
        const theme = darkMode ? '-dark' : ''
        const module = await import(`./lotties/${src}${theme}.json`)
        setAnimation(module.default ?? module)
      } catch (error) {
        console.error(`Failed to load animation: ${src}`, error)
        setAnimation(null)
      } finally {
        setIsLoading(false)
      }
    }

    loadAnimation()
  }, [src])

  if (isLoading) {
    return (
      <div
        className={clsx('inline-flex items-center justify-center', className)}
        style={{ width, height }}
        {...props}
      >
        <div className="animate-pulse bg-muted rounded-full w-12 h-12" />
      </div>
    )
  }

  if (!animation) {
    return null
  }

  return (
    <div
      className={clsx('inline-flex items-center justify-center', className)}
      style={{ width, height }}
      {...props}
    >
      <Lottie
        animationData={animation}
        autoplay={autoplay}
        loop={loop}
        style={{ width: '100%', height: '100%' }}
      />
    </div>
  )
}

export { SimpleLottie as Lottie }
